import { Button, Form, Modal, Popconfirm, Row, Space } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import {
  BulbOutlined,
  CalendarOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  FacebookFilled,
  MessageOutlined,
  SaveOutlined,
  SearchOutlined,
  ShopOutlined, UserOutlined,
} from '@ant-design/icons'
import { getCalendarIntegration } from '../../../../../../services/calendar-integration'
import { getChatInstance } from '../../../../../../services/chatIntegration'
import { getCatalogIntegration } from '../../../../../../services/catalog-integration'
import ActionFormItems from '../../../../../../components/FormItems/ActionFormItems'
import { getCRMIntegration } from "../../../../../../services/crmIntegration";
import { getMetaAdsIntegration } from '../../../../../../services/meta-ads-integration';
import { getFeegowIntegration } from '../../../../../../services/feegow-integration';
import { LinkOutlined } from '@ant-design/icons';

const initCards = [
  {
    key: 'WEBHOOK',
    title: 'Webhook',
    description: 'Realize uma requisição http customizada.',
    icon: <CloudUploadOutlined style={{ marginRight: 8 }} />,
  },
  {
    key: 'KNOWLEDGE',
    title: 'Conhecimento',
    description:
      'Consulta informações específicas do conhecimento do assistente.',
    icon: <SearchOutlined style={{ marginRight: 8 }} />,
  },
  {
    key: 'NATIVE',
    title: 'Nativo',
    description:
      'Crie botões, envie imagens, audios e muito mais nativamente.',
    icon: <BulbOutlined style={{ marginRight: 8 }} />,
  },
]

export default function ActionEdit({
  trigger,
  data,
  clear,
  addAction,
  updateAction,
  deleteAction,
}) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [cards, setCards] = useState(initCards)
  const [catalogIntegrationData, setCatalogIntegrationData] = useState({})
  const [metaAdsIntegrationId, setMetaAdsIntegrationId] = useState(null)
  const [form] = Form.useForm()

  const onCreate = useCallback(
    async (values) => {
      setSaving(true)
      await addAction(values)
        .then(clear)
        .finally(() => setSaving(false))
    },
    [addAction, clear]
  )

  const onDelete = useCallback(async () => {
    setDeleting(true)
    await deleteAction(data?.id)
      .then(() => {
        clear()
        form.resetFields()
      })
      .finally(() => setDeleting(false))
  }, [data, deleteAction, clear, form])

  const onUpdate = useCallback(
    async (values) => {
      setSaving(true)
      await updateAction(values)
        .then(() => {
          clear()
          form.resetFields()
        })
        .finally(() => setSaving(false))
    },
    [updateAction, clear, form]
  )

  const onFinish = () => {
    const formData = form.getFieldsValue()
    const firstAction =
      formData.actions && formData.actions.length > 0 ? formData.actions[0] : {}
    return data === -1 ? onCreate(firstAction) : onUpdate(firstAction)
  }

  const footer = () => (
    <Row justify="end">
      <div>
        <Popconfirm
          title="Tem certeza?"
          onConfirm={onDelete}
          description="Essa ação será deletada se você confirmar"
        >
          {data !== -1 && (
            <Button
              style={{ marginRight: 8 }}
              danger
              icon={<DeleteOutlined />}
              loading={deleting}
            >
              Deletar
            </Button>
          )}
        </Popconfirm>
        <Space>
          <Button onClick={clear} icon={<CloseOutlined />} loading={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={form.submit}
            icon={<SaveOutlined />}
            type={'primary'}
            loading={saving}
          >
            {data === -1 ? 'Criar' : 'Salvar'}
          </Button>
        </Space>
      </div>
    </Row>
  )

  const cancel = () => {
    clear()
  }

  useEffect(() => {
    if (!data) {
      setOpen(false)
      return
    }

    form.setFieldValue('generativeTriggerID', trigger.id)
    form.setFieldValue('assistantID', trigger.assistantID)
    setOpen(true)

    if (data === -1) {
      form.setFieldsValue({ actions: [{ type: 'WEBHOOK' }] })
    } else {
      const actionToEdit = { ...data, type: data.type || 'WEBHOOK' }
      const actions = [actionToEdit]
      form.setFieldsValue({ ...data, actions })
    }
  }, [data, form, trigger.assistantID, trigger.id])

  useEffect(() => {
    setLoading(true)
    Promise.all([
      getCalendarIntegration({ assistantID: trigger.assistantID }).then(
        ({ data }) => {
          if (data.length > 0) {
            setCards((old) => {
              if (old.some(c => c.key === 'GOOGLE_CALENDAR')) return old
              return [
                ...old,
                {
                  key: 'GOOGLE_CALENDAR',
                  title: 'Google Calendar',
                  description:
                    'Utilize funções da integração com Google Calendar',
                  icon: <CalendarOutlined style={{ marginRight: 8 }} />,
                },
              ]
            })
          }
        }
      ),
      getChatInstance({ assistantID: trigger.agentID }).then(({ data }) => {
        if (data.length > 0) {
          setCards((old) => {
            if (old.some(c => c.key === 'CHAT_INTEGRATION')) return old
            return [
              ...old,
              {
                key: 'CHAT_INTEGRATION',
                title: 'Integração de Chat',
                description: 'Utilize funções da integração com o chat',
                icon: <MessageOutlined style={{ marginRight: 8 }} />,
              },
            ]
          })
        }
      }),
      getCatalogIntegration(trigger.assistantID).then(({ data }) => {
        if (data.length > 0) {
          setCatalogIntegrationData(data)
          setCards((old) => {
            if (old.some(c => c.key === 'CATALOG_INTEGRATION')) return old
            return [
              ...old,
              {
                key: 'CATALOG_INTEGRATION',
                title: 'Integração de Catálogo',
                description: 'Utilize funções da integração com o catálogo',
                icon: <ShopOutlined style={{ marginRight: 8 }} />,
              },
            ]
          })
        }
      }),
      getCRMIntegration(trigger.assistantID).then(({ data }) => {
        if (data.length > 0) {
          setCatalogIntegrationData(data)
          setCards((old) => {
            if (old.some(c => c.key === 'CRM')) return old
            return [
              ...old,
              {
                key: 'CRM',
                title: 'CRM Nativo',
                description: 'Utilize funções do CRM nativo',
                icon: <UserOutlined style={{ marginRight: 8 }} />,
              },
            ]
          })
        }
      }),
      getMetaAdsIntegration({ assistantID: trigger.assistantID }).then(({ data }) => {
        if (data.length > 0) {
          setMetaAdsIntegrationId(data[0].id)
          setCards((old) => {
            if (old.some(c => c.key === 'META_ADS')) return old
            return [
              ...old,
              {
                key: 'META_ADS',
                title: 'Meta Ads',
                description: 'Envie eventos para o Pixel do Facebook',
                icon: <FacebookFilled style={{ marginRight: 8 }} />,
              },
            ]
          })
        }
      }),
      getFeegowIntegration({ assistantID: trigger.assistantID }).then(({ data }) => {
        if (data.length > 0) {
          setCards((old) => {
            if (old.some(c => c.key === 'FEEGOW')) return old
            return [
              ...old,
              {
                key: 'FEEGOW',
                title: 'Feegow',
                description: 'Acesse pacientes, agendamentos e muito mais no Feegow',
                icon: <LinkOutlined style={{ marginRight: 8 }} />,
              },
            ]
          })
        }
      }),
    ]).finally(() => setLoading(false))

    return () => setCards(initCards)
  }, [trigger.assistantID])

  return (
    <Modal
      width="80%"
      title="Ação"
      open={open}
      onCancel={cancel}
      footer={footer()}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <ActionFormItems
          cards={cards}
          loading={loading}
          form={form}
          trigger={trigger}
          initValues={data}
          catalogIntegrationData={catalogIntegrationData}
          metaAdsIntegrationId={metaAdsIntegrationId}
        />
      </Form>
    </Modal>
  )
}
